/**
 * Automation Management — CDCI wizard
 *
 * Flow for a NEW app:
 *   Step 0 (App)  → Step 1 (GitHub) → Step 2 (ConfigMap + Secret, optional) → Step 3 (Deployment Config) → Step 4 (Activate)
 *
 * ConfigMaps and Secrets share a step because they are the same thing to the workload:
 * two envFrom sources on the deployment. Only their storage differs.
 *
 * Flow for an EXISTING app:
 *   Step 0 (App) → Step 1 (GitHub) → Step 4 (Activate)
 *
 * Left sidebar lists existing automations; right panel hosts the wizard.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import config from '../Config';
import { authFetch } from '../functions/authFetch';
import { HandleResponse } from '../functions/HandleResponse';
import { tierLabel, mermadicHostForApp } from '../functions/clusterSwitch';
import { useSharedClusterPreview } from '../components/SharedClusterGate';
import './pages.css';

const BASE_DEPLOY = () => `${config.RUNNER_API}/v1/deployment`;
const BASE_GH = () => `${config.RUNNER_API}/v1/github`;
const BASE_DOMAIN = () => `${config.RUNNER_API}/v1/domain`;
const BASE_SECRET = () => `${config.RUNNER_API}/v1/secret`;

const TIER_CAPS = {
  dev:  { cpu_m: 2000,  memory_mi: 4096  },
  prod: { cpu_m: 8000,  memory_mi: 32768 },
};

const sanitize = (name) => {
  if (!name) return '';
  let s = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-');
  s = s.replace(/^-+/, '').replace(/-+$/, '');
  return s;
};

const getDeployedUrl = (appName, urlPrefix, customDomain) => {
  if (urlPrefix && customDomain) return `https://${urlPrefix}.${customDomain}`;
  return `https://${mermadicHostForApp(sanitize(appName))}`;
};

// ── step labels ───────────────────────────────────────────────────────────────
const STEP_APP    = 0;
const STEP_GH     = 1;
const STEP_CM     = 2;
const STEP_DEPLOY = 3;
const STEP_REVIEW = 4;

// ── pipeline status helpers ───────────────────────────────────────────────────
const PIPELINE_STATUS = {
  config_saved:     { label: 'Config Saved',     cls: 'am-status-saved'    },
  triggered:        { label: 'Triggered',         cls: 'am-status-triggered' },
  building:         { label: 'Building',          cls: 'am-status-building' },
  ready_to_deploy:  { label: 'Ready to Deploy',   cls: 'am-status-ready'    },
  deployed:         { label: 'Deployed',          cls: 'am-status-deployed' },
  build_failed:     { label: 'Build Failed',      cls: 'am-status-failed'   },
  deploy_failed:    { label: 'Deploy Failed',     cls: 'am-status-failed'   },
};

// Resolve the {label, cls} for a pipeline status, adjusting for job (build-only)
// pipelines where there is no deploy step — a finished build is "Build Complete".
const pipelineStatusFor = (status, isJob) => {
  if (!status) return null;
  if (isJob && status === 'ready_to_deploy') {
    return { label: 'Ready to Run', cls: 'am-status-deployed' };
  }
  return PIPELINE_STATUS[status] || null;
};

// Statuses where a pipeline run is actively in-flight — block new triggers
const PIPELINE_RUNNING_STATUSES = new Set(['triggered', 'building']);

// Statuses where a build has completed (CI webhook has fired) and a log exists
const POST_BUILD_STATUSES = new Set(['ready_to_deploy', 'deployed', 'build_failed', 'deploy_failed']);

function formatStatusTime(ts) {
  if (!ts) return null;
  const diff = Math.floor((Date.now() - new Date(ts + (ts.endsWith('Z') ? '' : 'Z')).getTime()) / 1000);
  if (diff < 5)    return 'just now';
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / (86400 * 30))}mo ago`;
}

// Mirror Python's _sanitize_branch_name: replace / and : with -, strip remaining non-alnum/dash/underscore
function sanitizeImageTag(name) {
  if (!name) return '';
  return name.replace(/[/:]/g, '-').replace(/[^a-zA-Z0-9\-_]/g, '');
}

export default function AutomationManagement() {
  const navigate = useNavigate();

  // Shared cluster: the wizard can be walked end to end, but nothing is saved,
  // activated, or granted GitHub access.
  const isPreview = useSharedClusterPreview();

  // ── cluster tier ─────────────────────────────────────────────────────────
  const [clusterTier, setClusterTier] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [viewAdvancedOpen, setViewAdvancedOpen] = useState(false);

  useEffect(() => {
    authFetch(`${BASE_DEPLOY()}/get-cluster-tier`, { method: 'GET' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.tier) setClusterTier(d.tier); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── sidebar: list of existing automations ─────────────────────────────────
  const [automations, setAutomations] = useState([]);
  const [autoLoading, setAutoLoading] = useState(true);
  const [deletingAuto, setDeletingAuto] = useState(null);
  const [deleteAutoConfirm, setDeleteAutoConfirm] = useState(null);

  // ── wizard visibility + edit mode ─────────────────────────────────────────
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(STEP_APP);

  // ── step 0: app ────────────────────────────────────────────────────────────
  const [appMode, setAppMode] = useState('new');       // 'new' | 'existing'
  const [automationType, setAutomationType] = useState('deployment'); // 'deployment' | 'job'
  const [newAppName, setNewAppName] = useState('');
  const [newImageName, setNewImageName] = useState('');
  const [existingApps, setExistingApps] = useState([]);
  const [existingAppsLoading, setExistingAppsLoading] = useState(false);
  const [selectedExistingApp, setSelectedExistingApp] = useState('');
  const [selectedExistingDoc, setSelectedExistingDoc] = useState(null); // full config

  // ── step 1: github ─────────────────────────────────────────────────────────
  const [ghConnected, setGhConnected] = useState(null); // null=checking
  const [ghConnecting, setGhConnecting] = useState(false);
  const ghPollRef = useRef(null);
  const ghPollTimeoutRef = useRef(null);
  const [ghRepos, setGhRepos] = useState([]);
  const [ghReposLoading, setGhReposLoading] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [branchInput, setBranchInput] = useState('');
  const [branchValidating, setBranchValidating] = useState(false);

  // ── step 2: configmap ──────────────────────────────────────────────────────
  const [cmAction, setCmAction] = useState('skip');    // 'skip' | 'upload' | 'existing'
  const [cmName, setCmName] = useState('');
  const [cmFile, setCmFile] = useState(null);
  const [existingCms, setExistingCms] = useState([]);
  const [selectedCm, setSelectedCm] = useState('');
  const [cmSaving, setCmSaving] = useState(false);
  const [cmMsg, setCmMsg] = useState(null);
  const [cmErr, setCmErr] = useState(null);
  const [cmDone, setCmDone] = useState(false);

  // ── step 2: secret (same step as configmap) ────────────────────────────────
  const [secAction, setSecAction] = useState('skip');  // 'skip' | 'upload' | 'existing'
  const [secName, setSecName] = useState('');
  const [secFile, setSecFile] = useState(null);
  const [existingSecrets, setExistingSecrets] = useState([]);
  const [selectedSec, setSelectedSec] = useState('');
  // No separate saving flag: one button saves both sections, so cmSaving covers it.
  const [secMsg, setSecMsg] = useState(null);
  const [secErr, setSecErr] = useState(null);

  // ── step 3: deployment config (new app only) ───────────────────────────────
  const [deplReplicas, setDeplReplicas] = useState(1);
  const [deplPort, setDeplPort] = useState(8080);
  const [deplCmRef, setDeplCmRef] = useState('');
  const [deplSecRef, setDeplSecRef] = useState('');
  const [deplUrlPrefix, setDeplUrlPrefix] = useState('');
  const [deplCustomDomain, setDeplCustomDomain] = useState('');
  const [deplCpuLimitM, setDeplCpuLimitM] = useState('');
  const [deplMemLimitMi, setDeplMemLimitMi] = useState('');

  // ── verified domains (for custom host in deploy config) ───────────────────
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  const [viewDeplUrlPrefix, setViewDeplUrlPrefix] = useState('');
  const [viewDeplCustomDomain, setViewDeplCustomDomain] = useState('');

  // ── step 4: activate ──────────────────────────────────────────────────────
  const [autoDeploy, setAutoDeploy] = useState(false);
  const [activating, setActivating] = useState(false);
  const [activateMsg, setActivateMsg] = useState(null);
  const [activateErr, setActivateErr] = useState(null);
  const [activateSuccess, setActivateSuccess] = useState(false);
  const [saveConfigBusy, setSaveConfigBusy] = useState(false);
  const [saveConfigMsg, setSaveConfigMsg] = useState(null);
  const [saveConfigErr, setSaveConfigErr] = useState(null);

  // ── view panel (existing automation) ──────────────────────────────────────
  const [viewingAuto, setViewingAuto] = useState(null);
  // 'select' = show action chooser (View Log / Edit); 'edit' = show wizard
  const [viewMode, setViewMode] = useState('select');
  const [viewTab, setViewTab] = useState(0);
  const [viewForm, setViewForm] = useState({});
  const [viewRepos, setViewRepos] = useState([]);
  const [viewReposLoading, setViewReposLoading] = useState(false);
  const [viewDeplReplicas, setViewDeplReplicas] = useState(1);
  const [viewDeplPort, setViewDeplPort] = useState(8080);
  const [viewDeplCpuLimitM, setViewDeplCpuLimitM] = useState('');
  const [viewDeplMemLimitMi, setViewDeplMemLimitMi] = useState('');
  const [viewDeplLoading, setViewDeplLoading] = useState(false);
  const [viewCmAction, setViewCmAction] = useState('existing'); // 'existing' | 'upload'
  const [viewCmName, setViewCmName] = useState('');
  const [viewCmFile, setViewCmFile] = useState(null);
  const [viewCmSaving, setViewCmSaving] = useState(false);
  const [viewCmMsg, setViewCmMsg] = useState(null);
  const [viewCmErr, setViewCmErr] = useState(null);
  const [viewSecAction, setViewSecAction] = useState('existing'); // 'existing' | 'upload'
  const [viewSecName, setViewSecName] = useState('');
  const [viewSecFile, setViewSecFile] = useState(null);
  const [viewSecSaving, setViewSecSaving] = useState(false);
  const [viewSecMsg, setViewSecMsg] = useState(null);
  const [viewSecErr, setViewSecErr] = useState(null);
  const [viewSaving, setViewSaving] = useState(false);
  const [viewActivating, setViewActivating] = useState(false);
  const [viewBranchValidating, setViewBranchValidating] = useState(false);
  const [viewMsg, setViewMsg] = useState(null);
  const [viewErr, setViewErr] = useState(null);
  const [viewSuccess, setViewSuccess] = useState(false);

  // ── step error ─────────────────────────────────────────────────────────────
  const [stepErr, setStepErr] = useState('');

  // ── derived ────────────────────────────────────────────────────────────────
  const effectiveAppName = appMode === 'new' ? sanitize(newAppName) : selectedExistingApp;
  const effectiveImageName = appMode === 'new' ? newImageName.trim() : (selectedExistingDoc?.image_name || '');
  const isNewApp = appMode === 'new';

  const isJobType = isNewApp && automationType === 'job';

  // An existing automation is a "job" (build-only) pipeline when its stored image_type is 'short'
  const viewIsJob = viewingAuto?.image_type === 'short';

  // Build-only pipelines just push an image to the registry, which the shared
  // cluster does. Build & deploy needs a cluster of your own, so on shared it
  // stays browsable but cannot be saved or activated.
  const previewLocked     = isPreview && !isJobType;
  const viewPreviewLocked = isPreview && !viewIsJob;

  // Steps to show for the progress bar
  const stepsForMode = (isNewApp && !isJobType)
    ? ['App', 'GitHub', 'Config & Secret', 'Deploy Config', 'Activate']
    : ['App', 'GitHub', 'Activate'];

  // Map logical step → progress index
  const progressIndex = () => {
    if (!isNewApp || isJobType) {
      if (step <= STEP_GH)     return step;
      return 2; // REVIEW maps to index 2
    }
    return step;
  };

  // ── fetch automations (sidebar) ────────────────────────────────────────────
  const fetchAutomations = useCallback(async () => {
    setAutoLoading(true);
    try {
      const res = await authFetch(`${BASE_GH()}/automation`, { method: 'GET' });
      await HandleResponse(res, navigate);
      if (res.ok) setAutomations((await res.json()) || []);
    } catch { }
    finally { setAutoLoading(false); }
  }, [navigate]);

  useEffect(() => { fetchAutomations(); }, [fetchAutomations]);

  // Poll every 30 s to keep pipeline status fresh
  useEffect(() => {
    const id = setInterval(fetchAutomations, 30_000);
    return () => clearInterval(id);
  }, [fetchAutomations]);

  // ── fetch existing apps ────────────────────────────────────────────────────
  const fetchExistingApps = useCallback(async () => {
    setExistingAppsLoading(true);
    try {
      const res = await authFetch(`${BASE_DEPLOY()}/list-all-deployment-config`, { method: 'GET' });
      if (res.ok) setExistingApps((await res.json()) || []);
    } catch { }
    finally { setExistingAppsLoading(false); }
  }, []);

  // ── fetch configmaps ───────────────────────────────────────────────────────
  const fetchCms = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_DEPLOY()}/list-configmap`, { method: 'GET' });
      if (res.ok) setExistingCms((await res.json()) || []);
    } catch { }
  }, []);

  // ── fetch secrets ──────────────────────────────────────────────────────────
  const fetchSecrets = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_SECRET()}/list`, { method: 'GET' });
      if (res.ok) setExistingSecrets((await res.json()) || []);
    } catch { }
  }, []);

  // ── check GitHub status ────────────────────────────────────────────────────
  const checkGhStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_GH()}/status`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        setGhConnected(!!data.connected);
        return !!data.connected;
      }
    } catch { }
    setGhConnected(false);
    return false;
  }, []);

  // ── fetch repos once connected ─────────────────────────────────────────────
  useEffect(() => {
    if (ghConnected && ghRepos.length === 0) {
      setGhReposLoading(true);
      authFetch(`${BASE_GH()}/repos`, { method: 'GET' })
        .then(r => r.ok ? r.json() : [])
        .then(d => setGhRepos(Array.isArray(d) ? d : []))
        .catch(() => {})
        .finally(() => setGhReposLoading(false));
    }
  }, [ghConnected, ghRepos.length]);

  // Clear branch input when repo changes
  useEffect(() => { setBranchInput(''); }, [selectedRepo]);

  // ── load existing app config when selected ─────────────────────────────────
  useEffect(() => {
    if (!selectedExistingApp) { setSelectedExistingDoc(null); return; }
    authFetch(`${BASE_DEPLOY()}/get-deployment-config?app_name=${encodeURIComponent(selectedExistingApp)}`, { method: 'GET' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setSelectedExistingDoc(d))
      .catch(() => {});
  }, [selectedExistingApp]);

  const stopGhPoll = useCallback(() => {
    if (ghPollRef.current) { clearInterval(ghPollRef.current); ghPollRef.current = null; }
    if (ghPollTimeoutRef.current) { clearTimeout(ghPollTimeoutRef.current); ghPollTimeoutRef.current = null; }
  }, []);

  useEffect(() => stopGhPoll, [stopGhPoll]);

  // ── open wizard ────────────────────────────────────────────────────────────
  const openWizard = () => {
    setWizardOpen(true);
    setStep(STEP_APP);
    setAppMode('new');
    setAutomationType('deployment');
    setNewAppName('');
    setNewImageName('');
    setSelectedExistingApp('');
    setSelectedExistingDoc(null);
    setGhConnected(null);
    setSelectedRepo('');
    setBranchInput('');
    setGhRepos([]);
    setCmAction('skip');
    setCmName('');
    setCmFile(null);
    setSelectedCm('');
    setCmDone(false);
    setCmMsg(null);
    setCmErr(null);
    setSecAction('skip');
    setSecName('');
    setSecFile(null);
    setSelectedSec('');
    setSecMsg(null);
    setSecErr(null);
    setDeplReplicas(1);
    setDeplPort(8080);
    setDeplCmRef('');
    setDeplSecRef('');
    setDeplUrlPrefix('');
    setDeplCustomDomain('');
    setAutoDeploy(false);
    setActivateMsg(null);
    setActivateErr(null);
    setActivateSuccess(false);
    setSaveConfigMsg(null);
    setSaveConfigErr(null);
    setSaveConfigBusy(false);
    setStepErr('');
    fetchExistingApps();
    fetchCms();
    fetchSecrets();
    fetchVerifiedDomains();
    checkGhStatus();
  };

  const closeWizard = () => { setWizardOpen(false); setActivateMsg(null); };

  // ── GitHub connect ─────────────────────────────────────────────────────────
  const handleGhConnect = async () => {
    setGhConnecting(true);
    try {
      const res = await authFetch(`${BASE_GH()}/connect`, { method: 'GET' });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank', 'noopener,noreferrer');
        stopGhPoll();
        ghPollRef.current = setInterval(async () => {
          const connected = await checkGhStatus();
          if (connected) stopGhPoll();
        }, 3000);
        // Auto-stop after 2 minutes if the user never completes installation
        ghPollTimeoutRef.current = setTimeout(stopGhPoll, 2 * 60 * 1000);
      }
    } catch { }
    finally { setGhConnecting(false); }
  };

  // ── step navigation ────────────────────────────────────────────────────────
  const nextStep = async () => {
    setStepErr('');

    if (step === STEP_APP) {
      if (isNewApp) {
        if (!sanitize(newAppName)) { setStepErr('App name is required.'); return; }
        if (!newImageName.trim()) { setStepErr('Image name (ECR tag) is required.'); return; }
      } else {
        if (!selectedExistingApp) { setStepErr('Please select an app.'); return; }
      }
      setStep(STEP_GH);
      return;
    }

    if (step === STEP_GH) {
      // A locked preview has nothing to connect for, so let the user walk past
      // this step and see the rest of the pipeline.
      if (previewLocked && !ghConnected) {
        setStep((isNewApp && !isJobType) ? STEP_CM : STEP_REVIEW);
        return;
      }
      if (!ghConnected)        { setStepErr('Connect your GitHub account first.'); return; }
      if (!selectedRepo)       { setStepErr('Select a repository.'); return; }
      if (!branchInput.trim()) { setStepErr('Enter a branch name.'); return; }
      // Validate branch exists
      setBranchValidating(true);
      setStepErr('');
      try {
        const res = await authFetch(
          `${BASE_GH()}/branches?repo=${encodeURIComponent(selectedRepo)}`,
          { method: 'GET' },
        );
        const branches = res.ok ? await res.json() : [];
        if (!Array.isArray(branches) || !branches.includes(branchInput.trim())) {
          setStepErr(`Branch "${branchInput.trim()}" was not found in ${selectedRepo}.`);
          return;
        }
      } catch {
        setStepErr('Could not verify the branch. Check your connection and try again.');
        return;
      } finally {
        setBranchValidating(false);
      }
      setStep((isNewApp && !isJobType) ? STEP_CM : STEP_REVIEW);
      return;
    }

    if (step === STEP_CM) {
      setStep(STEP_DEPLOY);
      return;
    }

    if (step === STEP_DEPLOY) {
      // Both env sources are optional: an app whose config is baked into the image
      // needs neither, and the backend treats an empty ref as "no ConfigMap/Secret".
      setStep(STEP_REVIEW);
      return;
    }
  };

  const prevStep = () => {
    setStepErr('');
    if (step === STEP_GH) { setStep(STEP_APP); return; }
    if (step === STEP_CM) { setStep(STEP_GH); return; }
    if (step === STEP_DEPLOY) { setStep(STEP_CM); return; }
    if (step === STEP_REVIEW) {
      setStep((isNewApp && !isJobType) ? STEP_DEPLOY : STEP_GH);
    }
  };

  // ── save ConfigMap + Secret (step 2) ───────────────────────────────────────
  // Both are resolved before advancing so the Deployment Config step opens with
  // whatever was just created already selectable.
  const resolveCm = async () => {
    if (cmAction === 'skip')     { setDeplCmRef(''); return true; }
    if (cmAction === 'existing') {
      if (!selectedCm) { setCmErr('Select a ConfigMap.'); return false; }
      setDeplCmRef(selectedCm);
      return true;
    }
    if (!cmName.trim()) { setCmErr('ConfigMap name is required.'); return false; }
    if (!cmFile)        { setCmErr('Select a .env file for the ConfigMap.'); return false; }
    const fd = new FormData();
    fd.append('env_file', cmFile);
    const res = await authFetch(
      `${BASE_DEPLOY()}/create-configmap-from-file?name=${encodeURIComponent(cmName.trim())}`,
      { method: 'POST', body: fd },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setCmErr(data.detail || 'Failed to create ConfigMap.'); return false; }
    setCmMsg(`ConfigMap "${cmName.trim()}" created.`);
    setDeplCmRef(cmName.trim());
    await fetchCms();
    return true;
  };

  const resolveSecret = async () => {
    if (secAction === 'skip')     { setDeplSecRef(''); return true; }
    if (secAction === 'existing') {
      if (!selectedSec) { setSecErr('Select a Secret.'); return false; }
      setDeplSecRef(selectedSec);
      return true;
    }
    if (!secName.trim()) { setSecErr('Secret name is required.'); return false; }
    if (!secFile)        { setSecErr('Select a .env file for the Secret.'); return false; }
    const fd = new FormData();
    fd.append('env_file', secFile);
    const res = await authFetch(
      `${BASE_SECRET()}/create-from-file?name=${encodeURIComponent(secName.trim())}`,
      { method: 'POST', body: fd },
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { setSecErr(data.detail || 'Failed to create Secret.'); return false; }
    setSecMsg(`Secret "${secName.trim()}" created.`);
    setDeplSecRef(secName.trim());
    await fetchSecrets();
    return true;
  };

  const handleSaveCm = async () => {
    setCmErr(null); setCmMsg(null);
    setSecErr(null); setSecMsg(null);
    const uploading = cmAction === 'upload' || secAction === 'upload';
    setCmSaving(true);
    try {
      if (!await resolveCm())     return;
      if (!await resolveSecret()) return;
      setCmDone(true);
      // Pause only when something was created, so the success line is readable.
      if (uploading) setTimeout(() => nextStep(), 600);
      else nextStep();
    } catch {
      setCmErr('Could not save. Check your connection and try again.');
    } finally { setCmSaving(false); }
  };

  // ── delete automation ──────────────────────────────────────────────────────
  const handleDeleteAuto = async (appName) => {
    if (isPreview && automations.find(a => a.app_name === appName)?.image_type !== 'short') return;
    setDeleteAutoConfirm(null);
    setDeletingAuto(appName);
    try {
      const res = await authFetch(
        `${BASE_GH()}/automation?app_name=${encodeURIComponent(appName)}`,
        { method: 'DELETE' },
      );
      if (res.ok) {
        setAutomations(prev => prev.filter(a => a.app_name !== appName));
        if (viewingAuto?.app_name === appName) setViewingAuto(null);
      }
    } catch { }
    finally { setDeletingAuto(null); }
  };

  // ── shared: build the automation POST body ─────────────────────────────────
  const buildAutoBody = () => ({
    app_name:        effectiveAppName,
    repo:            selectedRepo,
    branch:          branchInput.trim(),
    image_name:      effectiveImageName,
    image_type:      isJobType ? 'short' : 'long',
    auto_deploy:     isJobType ? false : autoDeploy,
    // deployment_name is always <app_name>-deployment (mirrors backend logic)
    deployment_name: `${effectiveAppName}-deployment`,
    // configmap/secret come from the deploy step (new app) or the existing deployment doc
    configmap_name:  isNewApp
      ? (deplCmRef || '')
      : (selectedExistingDoc?.configMapRef || ''),
    secret_name:     isNewApp
      ? (deplSecRef || '')
      : (selectedExistingDoc?.secretRef || ''),
  });

  // ── shared: save deployment config (new deployment-type apps only) ──────────
  const saveDeploymentIfNew = async () => {
    if (!isNewApp || isJobType) return true;
    const res = await authFetch(`${BASE_DEPLOY()}/save-deployment-config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appName:       sanitize(newAppName),
        replicas:      Number(deplReplicas),
        imageName:     newImageName.trim(),
        containerPort: Number(deplPort),
        configMapRef:  deplCmRef,
        secretRef:     deplSecRef,
        urlPrefix:     deplUrlPrefix.replace(/\./g, ''),
        customDomain:  deplCustomDomain,
        cpuLimitM:     deplCpuLimitM     !== '' ? Number(deplCpuLimitM)     : null,
        memoryLimitMi: deplMemLimitMi    !== '' ? Number(deplMemLimitMi)    : null,
      }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail || 'Failed to save deployment config.');
    }
    return true;
  };

  // ── save config (no build trigger) ─────────────────────────────────────────
  const handleSaveConfig = async () => {
    if (previewLocked) return;
    setSaveConfigBusy(true);
    setSaveConfigMsg(null);
    setSaveConfigErr(null);
    try {
      await saveDeploymentIfNew();
      const res = await authFetch(`${BASE_GH()}/automation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildAutoBody()),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSaveConfigMsg('Config saved.');
        await fetchAutomations();
      } else {
        setSaveConfigErr(data.detail || 'Failed to save config.');
      }
    } catch (err) { setSaveConfigErr(err.message || 'Something went wrong.'); }
    finally { setSaveConfigBusy(false); }
  };

  // ── activate (final step) ──────────────────────────────────────────────────
  const handleActivate = async () => {
    if (previewLocked) return;
    // Block if a build is already in-flight for this app
    const existingAuto = automations.find(a => a.app_name === effectiveAppName);
    if (existingAuto && PIPELINE_RUNNING_STATUSES.has(existingAuto.status)) {
      setActivateErr('A build is already in progress for this automation. Wait for it to finish before triggering a new one.');
      return;
    }
    setActivating(true);
    setActivateErr(null);
    setActivateMsg(null);
    setActivateSuccess(false);
    try {
      await saveDeploymentIfNew();

      // Save automation config (with configmap_name + deployment_name)
      const autoRes = await authFetch(`${BASE_GH()}/automation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildAutoBody()),
      });
      const autoData = await autoRes.json().catch(() => ({}));
      if (!autoRes.ok) {
        setActivateErr(autoData.detail || 'Failed to save automation config.');
        return;
      }

      // Trigger the initial build — backend validates GitHub access immediately and
      // returns 202; the zip download and commit happen in the background.
      const pushRes = await authFetch(`${BASE_GH()}/push-from-repo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo:       selectedRepo,
          branch:     branchInput.trim(),
          image_name: effectiveImageName,
          app_name:   effectiveAppName,
          image_type: isJobType ? 'short' : 'long',
        }),
      });
      const pushData = await pushRes.json().catch(() => ({}));
      if (!pushRes.ok) {
        setActivateSuccess(false);
        setActivateMsg(`Automation activated! However, the initial build could not be triggered: ${pushData.detail || 'unknown error'}. You can trigger it manually from the Registry page.`);
      } else {
        setActivateSuccess(true);
        setActivateMsg('Pipeline activated! The initial build is running in the background — track its progress in the sidebar.');
      }

      await fetchAutomations();
    } catch (err) { setActivateErr(err.message || 'Something went wrong.'); }
    finally { setActivating(false); }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  const renderProgress = () => (
    <div className="am-progress">
      {stepsForMode.map((label, i) => (
        <React.Fragment key={label}>
          <div className={`am-progress-step${i === progressIndex() ? ' active' : i < progressIndex() ? ' done' : ''}`}>
            <div className="am-progress-dot">{i < progressIndex() ? '✓' : i + 1}</div>
            <span className="am-progress-label">{label}</span>
          </div>
          {i < stepsForMode.length - 1 && <div className={`am-progress-line${i < progressIndex() ? ' done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStepApp = () => (
    <div className="am-step">
      <h4 className="am-step-title">Step 1 — App</h4>
      <div className="am-mode-toggle">
        <button
          type="button"
          className={`am-mode-btn${appMode === 'new' ? ' active' : ''}`}
          onClick={() => setAppMode('new')}
        >New App</button>
        <button
          type="button"
          className={`am-mode-btn${appMode === 'existing' ? ' active' : ''}`}
          onClick={() => { setAppMode('existing'); }}
        >Existing App</button>
      </div>

      {appMode === 'new' && (
        <>
          <div className="am-field">
            <label className="am-label">Automation Type</label>
            <div className="am-type-cards">
              <button
                type="button"
                className={`am-type-card${automationType === 'deployment' ? ' am-type-card-active' : ''}`}
                onClick={() => setAutomationType('deployment')}
              >
                <span className="am-type-card-icon">🚀</span>
                <span className="am-type-card-title">Build & Deploy</span>
                <span className="am-type-card-desc">Build your image and deploy it as a running service</span>
              </button>
              <button
                type="button"
                className={`am-type-card${automationType === 'job' ? ' am-type-card-active' : ''}`}
                onClick={() => setAutomationType('job')}
              >
                <span className="am-type-card-icon">⚙</span>
                <span className="am-type-card-title">Build Only</span>
                <span className="am-type-card-desc">Build and publish the image — use it for job runs, no deployment</span>
              </button>
            </div>
            {isPreview && (
              <span className="am-hint">
                Build Only runs on the shared cluster. Build &amp; Deploy needs a cluster of your own.
              </span>
            )}
          </div>
          <div className="am-field">
            <label className="am-label">App Name</label>
            <input
              className="cm-input am-input"
              value={newAppName}
              onChange={e => setNewAppName(e.target.value)}
              placeholder="my-api"
            />
            {newAppName && <span className="am-hint">Saved as: <code>{sanitize(newAppName)}</code></span>}
          </div>
          <div className="am-field">
            <label className="am-label">Image Name <span className="am-label-sub">(MCR image name)</span></label>
            <input
              className="cm-input am-input"
              value={newImageName}
              onChange={e => setNewImageName(e.target.value)}
              placeholder="my-api"
            />
            <span className="am-hint">The MCR name for your image.</span>
          </div>
        </>
      )}

      {appMode === 'existing' && (
        <div className="am-field">
          <label className="am-label">Select App {existingAppsLoading && <span className="am-label-sub">loading…</span>}</label>
          <select
            className="dm-select am-select"
            value={selectedExistingApp}
            onChange={e => setSelectedExistingApp(e.target.value)}
          >
            <option value="">— choose an app —</option>
            {existingApps.map(a => (
              <option key={a.app_name} value={a.app_name}>{a.app_name}</option>
            ))}
          </select>
          {isPreview && !existingAppsLoading && existingApps.length === 0 && (
            <span className="am-hint">
              Existing App attaches a pipeline to a deployment, and the shared cluster has none.
              Pick <strong>New App</strong> with Build Only to build and push an image right now.
            </span>
          )}
          {selectedExistingDoc && (
            <div className="am-existing-summary">
              <span>Image: <code>{selectedExistingDoc.image_name}</code></span>
              <span>ConfigMap: <code>{selectedExistingDoc.configMapRef || '— none —'}</code></span>
              <span>Secret: <code>{selectedExistingDoc.secretRef || '— none —'}</code></span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── fetch / refresh deployment config for the view panel ────────────────────
  const fetchViewDeplConfig = (appName) => {
    setViewDeplLoading(true);
    authFetch(
      `${BASE_DEPLOY()}/get-deployment-config?app_name=${encodeURIComponent(appName)}`,
      { method: 'GET' },
    )
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          setViewDeplReplicas(d.replicas ?? 1);
          setViewDeplPort(d.container_port ?? 8080);
          setViewDeplUrlPrefix(d.url_prefix || '');
          setViewDeplCustomDomain(d.custom_domain || '');
          setViewDeplCpuLimitM(d.cpu_limit_m  != null ? String(d.cpu_limit_m)  : '');
          setViewDeplMemLimitMi(d.memory_limit_mi != null ? String(d.memory_limit_mi) : '');
          setViewAdvancedOpen(d.cpu_limit_m != null || d.memory_limit_mi != null);
          if (d.configMapRef) {
            setViewForm(f => ({ ...f, configmap_name: f.configmap_name || d.configMapRef }));
          }
          if (d.secretRef) {
            setViewForm(f => ({ ...f, secret_name: f.secret_name || d.secretRef }));
          }
        }
      })
      .catch(() => {})
      .finally(() => setViewDeplLoading(false));
  };

  const fetchVerifiedDomains = useCallback(async () => {
    try {
      const res = await authFetch(`${BASE_DOMAIN()}/verified`, { method: 'GET' });
      if (res.ok) setVerifiedDomains((await res.json()) || []);
    } catch { }
  }, []);

  // ── open existing automation in view panel ─────────────────────────────────
  const openAutomation = (auto) => {
    const toForm = (doc) => ({
      image_name:     doc.image_name     || '',
      repo:           doc.repo           || '',
      branch:         doc.branch         || '',
      auto_deploy:    doc.auto_deploy    ?? false,
      configmap_name: doc.configmap_name || '',
      secret_name:    doc.secret_name    || '',
    });

    setViewingAuto(auto);
    setViewMode('select');
    setViewTab(0);
    setViewForm(toForm(auto));
    setViewMsg(null);
    setViewErr(null);
    setViewSuccess(false);
    setViewDeplReplicas(1);
    setViewDeplPort(8080);
    setViewDeplUrlPrefix('');
    setViewDeplCustomDomain('');
    setViewDeplCpuLimitM('');
    setViewDeplMemLimitMi('');
    setViewAdvancedOpen(false);
    setViewCmAction('existing');
    setViewCmName('');
    setViewCmFile(null);
    setViewCmMsg(null);
    setViewCmErr(null);
    setViewSecAction('existing');
    setViewSecName('');
    setViewSecFile(null);
    setViewSecMsg(null);
    setViewSecErr(null);
    setWizardOpen(false);

    // Fetch full automation doc (gets branch etc. that may be missing from list)
    authFetch(`${BASE_GH()}/automation/${encodeURIComponent(auto.app_name)}`, { method: 'GET' })
      .then(r => r.ok ? r.json() : null)
      .then(full => {
        if (full) { setViewingAuto(full); setViewForm(toForm(full)); }
      })
      .catch(() => {});

    // Fetch deployment config to pre-fill replicas / port
    fetchViewDeplConfig(auto.app_name);

    // Load repos for GitHub step
    if (viewRepos.length === 0) {
      setViewReposLoading(true);
      authFetch(`${BASE_GH()}/repos`, { method: 'GET' })
        .then(r => r.ok ? r.json() : [])
        .then(d => setViewRepos(Array.isArray(d) ? d : []))
        .catch(() => {})
        .finally(() => setViewReposLoading(false));
    }

    // Load configmaps + secrets for Step 3 and verified domains for Deploy Config
    fetchCms();
    fetchSecrets();
    fetchVerifiedDomains();
  };

  const validateViewBranch = async () => {
    if (!viewForm.repo || !viewForm.branch.trim()) return false;
    setViewBranchValidating(true);
    try {
      const res = await authFetch(
        `${BASE_GH()}/branches?repo=${encodeURIComponent(viewForm.repo)}`,
        { method: 'GET' },
      );
      const branches = res.ok ? await res.json() : [];
      if (!Array.isArray(branches) || !branches.includes(viewForm.branch.trim())) {
        setViewErr(`Branch "${viewForm.branch.trim()}" not found in ${viewForm.repo}.`);
        return false;
      }
    } catch {
      setViewErr('Could not verify the branch. Check your connection and try again.');
      return false;
    } finally {
      setViewBranchValidating(false);
    }
    return true;
  };

  // Shared helper: persist deployment config + automation config
  const saveViewConfigs = async () => {
    // 1. Save deployment config — deployment-type automations only (jobs have no deployment)
    if (!viewIsJob) {
      const deplRes = await authFetch(`${BASE_DEPLOY()}/save-deployment-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appName:       viewingAuto.app_name,
          replicas:      Number(viewDeplReplicas),
          imageName:     viewForm.image_name,
          containerPort: Number(viewDeplPort),
          configMapRef:  viewForm.configmap_name || '',
          secretRef:     viewForm.secret_name || '',
          urlPrefix:     viewDeplUrlPrefix.replace(/\./g, ''),
          customDomain:  viewDeplCustomDomain,
          cpuLimitM:     viewDeplCpuLimitM     !== '' ? Number(viewDeplCpuLimitM)     : null,
          memoryLimitMi: viewDeplMemLimitMi    !== '' ? Number(viewDeplMemLimitMi)    : null,
        }),
      });
      if (!deplRes.ok) {
        const d = await deplRes.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to save deployment config.');
      }
    }

    // 2. Save automation config (repo / branch / auto_deploy are NOT covered by deployment save).
    //    Always send image_type so a job pipeline is never silently flipped to a deployment.
    const autoRes = await authFetch(`${BASE_GH()}/automation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        app_name:       viewingAuto.app_name,
        repo:           viewForm.repo,
        branch:         viewForm.branch.trim(),
        image_name:     viewForm.image_name,
        image_type:     viewIsJob ? 'short' : 'long',
        configmap_name: viewIsJob ? '' : (viewForm.configmap_name || ''),
        secret_name:    viewIsJob ? '' : (viewForm.secret_name || ''),
        auto_deploy:    viewIsJob ? false : viewForm.auto_deploy,
      }),
    });
    if (!autoRes.ok) {
      const d = await autoRes.json().catch(() => ({}));
      throw new Error(d.detail || 'Failed to save automation config.');
    }
  };

  const handleViewSave = async () => {
    if (viewPreviewLocked) return;
    setViewSaving(true);
    setViewMsg(null);
    setViewErr(null);
    setViewSuccess(false);
    const valid = await validateViewBranch();
    if (!valid) { setViewSaving(false); return; }
    try {
      await saveViewConfigs();
      setViewSuccess(true);
      setViewMsg('Config saved.');
      setViewingAuto(prev => ({ ...prev, ...viewForm }));
      await fetchAutomations();
    } catch (err) { setViewErr(err.message || 'Save failed.'); }
    finally { setViewSaving(false); }
  };

  const handleViewActivate = async () => {
    if (viewPreviewLocked) return;
    // Block if a build is already in-flight
    if (PIPELINE_RUNNING_STATUSES.has(viewingAuto?.status)) {
      setViewErr('A build is already in progress. Wait for it to complete before triggering a new run.');
      return;
    }
    setViewActivating(true);
    setViewMsg(null);
    setViewErr(null);
    setViewSuccess(false);
    const valid = await validateViewBranch();
    if (!valid) { setViewActivating(false); return; }
    try {
      await saveViewConfigs();
      // Backend validates GitHub access immediately and returns 202; build runs in background.
      const pushRes = await authFetch(`${BASE_GH()}/push-from-repo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          repo:       viewForm.repo,
          branch:     viewForm.branch.trim(),
          image_name: viewForm.image_name,
          app_name:   viewingAuto.app_name,
          image_type: viewIsJob ? 'short' : 'long',
        }),
      });
      const pushData = await pushRes.json().catch(() => ({}));
      if (!pushRes.ok) {
        setViewErr(`Config saved, but build could not be triggered: ${pushData.detail || 'unknown error'}.`);
      } else {
        setViewSuccess(true);
        setViewMsg('Config saved! Build is running in the background — track progress in the sidebar.');
      }
      setViewingAuto(prev => ({ ...prev, ...viewForm }));
      await fetchAutomations();
    } catch { setViewErr('Something went wrong.'); }
    finally { setViewActivating(false); }
  };

  const VIEW_STEPS = viewIsJob
    ? ['App', 'GitHub', 'Activate']
    : ['App', 'GitHub', 'Config & Secret', 'Deploy Config', 'Activate'];

  const renderActionChooser = () => {
    if (!viewingAuto) return null;
    const status = viewingAuto.status;
    const s = pipelineStatusFor(status, viewIsJob);
    const timeStr = formatStatusTime(viewingAuto.status_updated_at);
    const hasLog = status && POST_BUILD_STATUSES.has(status);
    return (
      <div className="am-action-chooser">
        <div className="am-wizard-header">
          <h3 className="dm-panel-title">{viewingAuto.app_name}</h3>
          <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => { setViewingAuto(null); setViewMode('select'); }}>✕</button>
        </div>

        <div className="am-action-chooser-info">
          <div className="am-action-chooser-repo">
            <span>{viewingAuto.repo || '—'}</span>
            {viewingAuto.branch && <span className="am-sidebar-branch">@{viewingAuto.branch}</span>}
            <span className="am-type-tag">{viewIsJob ? 'Build Only' : 'Build & Deploy'}</span>
          </div>
          {status && (
            <div className="am-action-chooser-status">
              <span className={`am-status-badge ${s ? s.cls : ''}`}>
                {s ? s.label : status}
              </span>
              {timeStr && <span className="am-status-time">{timeStr}</span>}
            </div>
          )}
          {status === 'deployed' && (
            <div className="am-deployed-link-row">
              <span className="am-deployed-live-dot" title="Live" />
              <a
                className="am-deployed-link"
                href={getDeployedUrl(viewingAuto.app_name, viewDeplUrlPrefix, viewDeplCustomDomain)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getDeployedUrl(viewingAuto.app_name, viewDeplUrlPrefix, viewDeplCustomDomain)}
              </a>
            </div>
          )}
        </div>

        <div className="am-action-chooser-cards">
          <button
            type="button"
            className="am-action-card"
            onClick={() => setViewMode('edit')}
          >
            <span className="am-action-card-icon">⚙</span>
            <span className="am-action-card-label">Edit / Activate</span>
            <span className="am-action-card-sub">Configure repo, branch, and trigger a build</span>
          </button>
          <button
            type="button"
            className={`am-action-card${!hasLog || !viewingAuto.image_name ? ' am-action-card-disabled' : ''}`}
            disabled={!hasLog || !viewingAuto.image_name}
            title={!hasLog ? 'No completed build yet' : 'View build log in Build History'}
            onClick={() => navigate('/mcr/push-history', { state: { autoSelectImage: sanitizeImageTag(viewingAuto.image_name) } })}
          >
            <span className="am-action-card-icon">📋</span>
            <span className="am-action-card-label">View Build Log</span>
            <span className="am-action-card-sub">{hasLog ? 'Open the latest CI build output' : 'No completed build yet'}</span>
          </button>
        </div>
      </div>
    );
  };

  const renderViewPanel = () => {
    if (!viewingAuto) return null;
    const busy = viewSaving || viewActivating || viewBranchValidating;
    const isLast  = viewTab === VIEW_STEPS.length - 1;
    const isFirst = viewTab === 0;

    const renderViewProgress = () => (
      <div className="am-progress">
        {VIEW_STEPS.map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`am-progress-step${viewTab === i ? ' active' : viewTab > i ? ' done' : ''}`}
              onClick={() => {
                if (busy) return;
                setViewTab(i);
                // Step 4 (Deploy Config): always refresh from deployment collection
                if (i === 3 && viewingAuto) { fetchViewDeplConfig(viewingAuto.app_name); fetchVerifiedDomains(); }
              }}
              style={{ cursor: busy ? 'default' : 'pointer' }}
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' && !busy) {
                  setViewTab(i);
                  if (i === 3 && viewingAuto) { fetchViewDeplConfig(viewingAuto.app_name); fetchVerifiedDomains(); }
                }
              }}
              aria-label={`Go to ${label}`}
            >
              <div className="am-progress-dot">{viewTab > i ? '✓' : i + 1}</div>
              <span className="am-progress-label">{label}</span>
            </div>
            {i < VIEW_STEPS.length - 1 && (
              <div className={`am-progress-line${viewTab > i ? ' done' : ''}`} />
            )}
          </React.Fragment>
        ))}
      </div>
    );

    const renderViewStep = () => {
      // ── Step 1: App ──────────────────────────────────────────────────────────
      if (viewTab === 0) return (
        <div className="am-step">
          <h4 className="am-step-title">Step 1 — App</h4>
          <div className="am-field">
            <label className="am-label">App Name <span className="am-label-sub">(read-only)</span></label>
            <input className="cm-input am-input dm-input-readonly" value={viewingAuto.app_name} readOnly />
          </div>
          <div className="am-field">
            <label className="am-label">Image Name</label>
            <input
              className="cm-input am-input"
              value={viewForm.image_name}
              onChange={e => setViewForm(f => ({ ...f, image_name: e.target.value }))}
              disabled={busy}
              placeholder="my-api"
            />
          </div>
        </div>
      );

      // ── Step 2: GitHub ───────────────────────────────────────────────────────
      if (viewTab === 1) return (
        <div className="am-step">
          <h4 className="am-step-title">Step 2 — GitHub</h4>
          <div className="am-gh-connected-row">
            <button
              type="button"
              className="gh-access-btn"
              onClick={handleGhConnect}
              disabled={ghConnecting}
              title="Open GitHub to add or remove repositories from this installation"
            >
              <svg height="14" viewBox="0 0 16 16" width="14" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
              {ghConnecting ? 'Opening…' : 'Edit GitHub Access'}
            </button>
            <div className="am-gh-badge">✓ GitHub connected</div>
          </div>
          {ghConnecting && (
            <p className="am-hint" style={{ marginBottom: '0.5rem' }}>
              Update repo access in the new tab, then return here.
            </p>
          )}
          <div className="am-field">
            <div className="gh-label-row">
              <label className="am-label" htmlFor="am-view-repo">
                Repository {viewReposLoading && <span className="am-label-sub">loading…</span>}
              </label>
              <button
                type="button"
                className="am-refresh-btn"
                onClick={() => {
                  setViewReposLoading(true);
                  authFetch(`${BASE_GH()}/repos`, { method: 'GET' })
                    .then(r => r.ok ? r.json() : [])
                    .then(d => setViewRepos(Array.isArray(d) ? d : []))
                    .catch(() => {})
                    .finally(() => setViewReposLoading(false));
                }}
                disabled={busy || viewReposLoading}
                title="Refresh repository list"
              >
                {viewReposLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>
            <select
              id="am-view-repo"
              className="dm-select am-select"
              value={viewForm.repo}
              onChange={e => setViewForm(f => ({ ...f, repo: e.target.value }))}
              disabled={busy || viewReposLoading}
            >
              <option value="">— select repo —</option>
              {viewForm.repo && !viewRepos.find(r => r.full_name === viewForm.repo) && (
                <option value={viewForm.repo}>{viewForm.repo}</option>
              )}
              {viewRepos.map(r => (
                <option key={r.full_name} value={r.full_name}>{r.full_name}</option>
              ))}
            </select>
          </div>
          <div className="am-field">
            <label className="am-label">Branch</label>
            <input
              className="cm-input am-input"
              value={viewForm.branch}
              onChange={e => setViewForm(f => ({ ...f, branch: e.target.value }))}
              placeholder="e.g. main"
              disabled={busy}
              spellCheck={false}
            />
            <span className="am-hint">Branch will be verified when you save or activate.</span>
          </div>
        </div>
      );

      // ── Step 3: ConfigMap (deployment-type only) ──────────────────────────────
      if (!viewIsJob && viewTab === 2) {
        const handleViewCreateCm = async () => {
          if (!viewCmName.trim()) { setViewCmErr('ConfigMap name is required.'); return; }
          if (!viewCmFile)        { setViewCmErr('Select a .env file.'); return; }
          setViewCmSaving(true);
          setViewCmErr(null);
          setViewCmMsg(null);
          try {
            const fd = new FormData();
            fd.append('env_file', viewCmFile);
            const res = await authFetch(
              `${BASE_DEPLOY()}/create-configmap-from-file?name=${encodeURIComponent(viewCmName.trim())}`,
              { method: 'POST', body: fd },
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setViewCmMsg(`ConfigMap "${viewCmName.trim()}" created.`);
              setViewForm(f => ({ ...f, configmap_name: viewCmName.trim() }));
              await fetchCms();
              setViewCmAction('existing');
            } else {
              setViewCmErr(data.detail || 'Failed to create ConfigMap.');
            }
          } catch { setViewCmErr('Failed to create ConfigMap.'); }
          finally { setViewCmSaving(false); }
        };

        const handleViewCreateSecret = async () => {
          if (!viewSecName.trim()) { setViewSecErr('Secret name is required.'); return; }
          if (!viewSecFile)        { setViewSecErr('Select a .env file.'); return; }
          setViewSecSaving(true);
          setViewSecErr(null);
          setViewSecMsg(null);
          try {
            const fd = new FormData();
            fd.append('env_file', viewSecFile);
            const res = await authFetch(
              `${BASE_SECRET()}/create-from-file?name=${encodeURIComponent(viewSecName.trim())}`,
              { method: 'POST', body: fd },
            );
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
              setViewSecMsg(`Secret "${viewSecName.trim()}" created.`);
              setViewForm(f => ({ ...f, secret_name: viewSecName.trim() }));
              await fetchSecrets();
              setViewSecAction('existing');
            } else {
              setViewSecErr(data.detail || 'Failed to create Secret.');
            }
          } catch { setViewSecErr('Failed to create Secret.'); }
          finally { setViewSecSaving(false); }
        };

        return (
          <div className="am-step">
            <h4 className="am-step-title">Step 3 — ConfigMap &amp; Secret <span className="am-step-optional">(both optional)</span></h4>
            <p className="am-hint">ConfigMaps hold environment variables for your deployment.</p>
            <div className="am-cm-tabs">
              {[{ id: 'existing', label: 'Use Existing' }, { id: 'upload', label: 'Upload .env' }].map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`am-cm-tab${viewCmAction === t.id ? ' active' : ''}`}
                  onClick={() => { setViewCmAction(t.id); setViewCmErr(null); setViewCmMsg(null); }}
                  disabled={busy}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {viewCmAction === 'existing' && (
              <div className="am-field" style={{ marginTop: '0.75rem' }}>
                <label className="am-label">Select ConfigMap</label>
                <select
                  className="dm-select am-select"
                  value={viewForm.configmap_name}
                  onChange={e => setViewForm(f => ({ ...f, configmap_name: e.target.value }))}
                  disabled={busy}
                >
                  <option value="">— none —</option>
                  {existingCms.map(cm => <option key={cm} value={cm}>{cm}</option>)}
                </select>
              </div>
            )}

            {viewCmAction === 'upload' && (
              <div style={{ marginTop: '0.75rem' }}>
                <div className="am-field">
                  <label className="am-label">ConfigMap Name</label>
                  <input
                    className="cm-input am-input"
                    value={viewCmName}
                    onChange={e => setViewCmName(e.target.value)}
                    placeholder={`${viewingAuto.app_name}-config`}
                    disabled={viewCmSaving}
                  />
                </div>
                <div className="am-field">
                  <label className="am-label">.env File</label>
                  <div className="mcr-file-upload">
                    <input
                      id="view-cm-file"
                      type="file"
                      accept=".env,text/plain"
                      onChange={e => setViewCmFile(e.target.files?.[0] || null)}
                      className="mcr-file-input-hidden"
                    />
                    <button
                      type="button"
                      className="mcr-select-file-btn"
                      onClick={() => document.getElementById('view-cm-file')?.click()}
                      disabled={viewCmSaving}
                    >
                      Select file
                    </button>
                    {viewCmFile && <span className="mcr-file-name">{viewCmFile.name}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  className="am-btn-next"
                  onClick={handleViewCreateCm}
                  disabled={viewCmSaving || busy}
                  style={{ marginTop: '0.25rem' }}
                >
                  {viewCmSaving ? 'Creating…' : 'Create ConfigMap'}
                </button>
              </div>
            )}

            {viewCmMsg && <p className="cm-success" style={{ marginTop: '0.5rem' }}>{viewCmMsg}</p>}
            {viewCmErr && <p className="cm-error"  style={{ marginTop: '0.5rem' }}>{viewCmErr}</p>}

            <h4 className="am-step-title" style={{ marginTop: '1.5rem' }}>
              Secret <span className="am-step-optional">(optional)</span>
            </h4>
            <p className="am-hint">
              Secrets hold sensitive environment variables. Values are stored encrypted and never shown again.
            </p>
            <div className="am-cm-tabs">
              {[{ id: 'existing', label: 'Use Existing' }, { id: 'upload', label: 'Upload .env' }].map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`am-cm-tab${viewSecAction === t.id ? ' active' : ''}`}
                  onClick={() => { setViewSecAction(t.id); setViewSecErr(null); setViewSecMsg(null); }}
                  disabled={busy}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {viewSecAction === 'existing' && (
              <div className="am-field" style={{ marginTop: '0.75rem' }}>
                <label className="am-label">Select Secret</label>
                <select
                  className="dm-select am-select"
                  value={viewForm.secret_name || ''}
                  onChange={e => setViewForm(f => ({ ...f, secret_name: e.target.value }))}
                  disabled={busy}
                >
                  <option value="">— none —</option>
                  {existingSecrets.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {viewSecAction === 'upload' && (
              <div style={{ marginTop: '0.75rem' }}>
                <div className="am-field">
                  <label className="am-label">Secret Name</label>
                  <input
                    className="cm-input am-input"
                    value={viewSecName}
                    onChange={e => setViewSecName(e.target.value)}
                    placeholder={`${viewingAuto.app_name}-secret`}
                    disabled={viewSecSaving}
                  />
                </div>
                <div className="am-field">
                  <label className="am-label">.env File</label>
                  <div className="mcr-file-upload">
                    <input
                      id="view-sec-file"
                      type="file"
                      accept=".env,text/plain"
                      onChange={e => setViewSecFile(e.target.files?.[0] || null)}
                      className="mcr-file-input-hidden"
                    />
                    <button
                      type="button"
                      className="mcr-select-file-btn"
                      onClick={() => document.getElementById('view-sec-file')?.click()}
                      disabled={viewSecSaving}
                    >
                      Select file
                    </button>
                    {viewSecFile && <span className="mcr-file-name">{viewSecFile.name}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  className="am-btn-next"
                  onClick={handleViewCreateSecret}
                  disabled={viewSecSaving || busy}
                  style={{ marginTop: '0.25rem' }}
                >
                  {viewSecSaving ? 'Creating…' : 'Create Secret'}
                </button>
              </div>
            )}

            {viewSecMsg && <p className="cm-success" style={{ marginTop: '0.5rem' }}>{viewSecMsg}</p>}
            {viewSecErr && <p className="cm-error"  style={{ marginTop: '0.5rem' }}>{viewSecErr}</p>}
          </div>
        );
      }

      // ── Step 4: Deploy Config (deployment-type only) ──────────────────────────
      if (!viewIsJob && viewTab === 3) return (
        <div className="am-step">
          <h4 className="am-step-title">Step 4 — Deployment Config</h4>
          {viewDeplLoading && <p className="am-hint">Loading deployment config…</p>}
          <div className="dm-row2">
            <div className="am-field">
              <label className="am-label">Replicas</label>
              <input
                className="cm-input dm-input dm-input-sm"
                type="number"
                min="1"
                value={viewDeplReplicas}
                onChange={e => setViewDeplReplicas(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="am-field">
              <label className="am-label">Container Port</label>
              <input
                className="cm-input dm-input dm-input-sm dm-no-spinner"
                type="number"
                min="1"
                max="65535"
                value={viewDeplPort}
                onChange={e => setViewDeplPort(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>
          <div className="am-field">
            <label className="am-label">Image Name <span className="am-label-sub">(from App step)</span></label>
            <input className="cm-input am-input dm-input-readonly" value={viewForm.image_name || '—'} readOnly />
          </div>
          <div className="am-field">
            <label className="am-label">ConfigMap <span className="am-label-sub">(from ConfigMap &amp; Secret step)</span></label>
            <input className="cm-input am-input dm-input-readonly" value={viewForm.configmap_name || '— none —'} readOnly />
          </div>
          <div className="am-field">
            <label className="am-label">Secret <span className="am-label-sub">(from ConfigMap &amp; Secret step)</span></label>
            <input className="cm-input am-input dm-input-readonly" value={viewForm.secret_name || '— none —'} readOnly />
          </div>
          <div className="am-field">
            <label className="am-label">URL Prefix <span className="am-label-sub">(optional, no dots)</span></label>
            <input
              className="cm-input am-input"
              type="text"
              placeholder="prefix"
              value={viewDeplUrlPrefix}
              onChange={e => setViewDeplUrlPrefix(e.target.value.replace(/\./g, ''))}
              disabled={busy}
            />
          </div>
          <div className="am-field">
            <label className="am-label">Custom Domain <span className="am-label-sub">(optional)</span></label>
            <select
              className="cm-input am-input"
              value={viewDeplCustomDomain}
              onChange={e => setViewDeplCustomDomain(e.target.value)}
              disabled={busy}
            >
              <option value="">— None —</option>
              {verifiedDomains.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {viewDeplUrlPrefix && viewDeplCustomDomain && (
            <p className="am-hint" style={{ marginTop: '0.35rem' }}>
              Host: <code>{viewDeplUrlPrefix}.{viewDeplCustomDomain}</code>
            </p>
          )}

          {clusterTier && TIER_CAPS[clusterTier] && (() => {
            const caps = TIER_CAPS[clusterTier];
            return (
              <div className="am-field">
                <button
                  type="button"
                  className="dm-advanced-toggle"
                  onClick={() => setViewAdvancedOpen(o => !o)}
                  disabled={busy}
                >
                  {viewAdvancedOpen ? '▾' : '▸'} Advanced Settings
                </button>
                {viewAdvancedOpen && (
                  <div className="dm-advanced-body">
                    <p className="am-hint">
                      Resource limits per pod. Leave blank to use cluster defaults (500m CPU / 512Mi memory).
                      Max for <strong>{tierLabel(clusterTier)}</strong> tier: {caps.cpu_m}m CPU / {caps.memory_mi}Mi memory.
                    </p>
                    <div className="dm-row2">
                      <div className="am-field">
                        <label className="am-label">CPU Limit (millicores)</label>
                        <input
                          className="cm-input dm-input dm-input-sm dm-no-spinner"
                          type="number"
                          min="100"
                          max={caps.cpu_m}
                          step="100"
                          placeholder={`e.g. 500 (max ${caps.cpu_m})`}
                          value={viewDeplCpuLimitM}
                          onChange={e => setViewDeplCpuLimitM(e.target.value)}
                          disabled={busy}
                        />
                      </div>
                      <div className="am-field">
                        <label className="am-label">Memory Limit (MiB)</label>
                        <input
                          className="cm-input dm-input dm-input-sm dm-no-spinner"
                          type="number"
                          min="128"
                          max={caps.memory_mi}
                          step="128"
                          placeholder={`e.g. 512 (max ${caps.memory_mi})`}
                          value={viewDeplMemLimitMi}
                          onChange={e => setViewDeplMemLimitMi(e.target.value)}
                          disabled={busy}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      );

      // ── Final step: Activate ─────────────────────────────────────────────────
      return (
        <div className="am-step">
          <h4 className="am-step-title">Step {VIEW_STEPS.length} — Review & Activate</h4>
          <div className="am-review-grid">
            <span className="am-review-label">App</span>
            <span className="am-review-val"><code>{viewingAuto.app_name}</code></span>
            <span className="am-review-label">Image Name</span>
            <span className="am-review-val"><code>{viewForm.image_name || '—'}</code></span>
            <span className="am-review-label">Repository</span>
            <span className="am-review-val"><code>{viewForm.repo || '—'}</code></span>
            <span className="am-review-label">Branch</span>
            <span className="am-review-val"><code>{viewForm.branch || '—'}</code></span>
            <span className="am-review-label">Pipeline Type</span>
            <span className="am-review-val">{viewIsJob ? 'Build Only (job image)' : 'Build & Deploy'}</span>
            {!viewIsJob && viewForm.configmap_name && <>
              <span className="am-review-label">ConfigMap</span>
              <span className="am-review-val"><code>{viewForm.configmap_name}</code></span>
            </>}
            {!viewIsJob && viewForm.secret_name && <>
              <span className="am-review-label">Secret</span>
              <span className="am-review-val"><code>{viewForm.secret_name}</code></span>
            </>}
          </div>
          {viewIsJob ? (
            <p className="am-hint" style={{ marginTop: '0.75rem' }}>
              Build Only — each push builds the image and pushes it to MCR for use in job runs. No deployment is triggered.
            </p>
          ) : (
            <div className="am-field" style={{ marginTop: '0.75rem' }}>
              <label className="am-label">Auto-deploy on build success</label>
              <div className="am-toggle-row">
                <button
                  type="button"
                  className={`am-toggle${viewForm.auto_deploy ? ' am-toggle-on' : ''}`}
                  onClick={() => !busy && setViewForm(f => ({ ...f, auto_deploy: !f.auto_deploy }))}
                  aria-pressed={viewForm.auto_deploy}
                >
                  <span className="am-toggle-knob" />
                </button>
                <span className="am-hint" style={{ marginLeft: '0.6rem' }}>
                  {viewForm.auto_deploy
                    ? 'On — every successful build will redeploy automatically.'
                    : 'Off — you will be notified by email when the build is ready.'}
                </span>
              </div>
            </div>
          )}
          {PIPELINE_RUNNING_STATUSES.has(viewingAuto?.status) && (
            <div className="am-pipeline-running-banner">
              <span className="am-pipeline-running-dot" />
              <span>
                A build is already <strong>{PIPELINE_STATUS[viewingAuto.status]?.label ?? viewingAuto.status}</strong>.
                Wait for it to finish before triggering a new run.
              </span>
            </div>
          )}
          {viewErr && <p className="cm-error">{viewErr}</p>}
          {viewMsg && <p className={viewSuccess ? 'cm-success' : 'cm-error'}>{viewMsg}</p>}
        </div>
      );
    };

    const viewPipelineRunning = isLast && PIPELINE_RUNNING_STATUSES.has(viewingAuto?.status);
    const renderViewNav = () => (
      <div className="am-nav">
        {!isFirst && (
          <button type="button" className="am-btn-back" onClick={() => setViewTab(t => t - 1)} disabled={busy}>
            ← Back
          </button>
        )}
        <div style={{ flex: 1 }} />
        {isLast ? (
          <>
            <button type="button" className="dm-btn-secondary" onClick={handleViewSave} disabled={viewPreviewLocked || busy} title={viewPreviewLocked ? 'Build & Deploy needs a dedicated cluster' : undefined}>
              {viewSaving ? 'Saving…' : viewBranchValidating ? 'Verifying…' : 'Save Config'}
            </button>
            <button type="button" className="am-btn-activate" onClick={handleViewActivate} disabled={viewPreviewLocked || busy || viewPipelineRunning} title={viewPreviewLocked ? 'Build & Deploy needs a dedicated cluster' : undefined} style={{ marginLeft: '0.5rem' }}>
              {viewActivating ? 'Activating…' : viewBranchValidating ? 'Verifying…' : viewPipelineRunning ? '⏳ Build in Progress' : '▶ Activate Automation'}
            </button>
          </>
        ) : (
          <button type="button" className="am-btn-next" onClick={() => setViewTab(t => t + 1)} disabled={busy}>
            Next →
          </button>
        )}
      </div>
    );

    return (
      <div className="am-wizard">
        <div className="am-wizard-header">
          <h3 className="dm-panel-title">{viewingAuto.app_name}</h3>
          <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={() => { setViewingAuto(null); setViewMode('select'); }}>✕</button>
        </div>
        {renderViewProgress()}
        {renderViewStep()}
        {renderViewNav()}
      </div>
    );
  };

  const renderStepGitHub = () => (
    <div className="am-step">
      <h4 className="am-step-title">Step 2 — GitHub</h4>

      {ghConnected === null && <p className="am-hint">Checking GitHub connection…</p>}

      {ghConnected === false && (
        <div className="am-gh-connect">
          <p className="am-hint">Install the Mermadic GitHub App to grant access to your repositories.</p>
          <button
            type="button"
            className="am-gh-connect-btn"
            onClick={handleGhConnect}
            disabled={ghConnecting}
          >
            {ghConnecting ? 'Opening GitHub…' : '⎆ Connect GitHub App'}
          </button>
          {previewLocked && (
            <p className="am-hint" style={{ marginTop: '0.5rem' }}>
              Build &amp; Deploy cannot run on the shared cluster, so there is nothing to connect for
              yet. Click Next to see the rest of the pipeline, or go back and pick Build Only, which
              builds and pushes right now.
            </p>
          )}
          {ghConnecting && (
            <p className="am-hint" style={{ marginTop: '0.5rem' }}>
              Install the App in the new tab, then return here — checking automatically…
            </p>
          )}
        </div>
      )}

      {ghConnected === true && (
        <>
          <div className="am-gh-connected-row">
            <button
              type="button"
              className="gh-access-btn"
              onClick={handleGhConnect}
              disabled={ghConnecting}
              title="Open GitHub to add or remove repositories from this installation"
            >
              <svg height="14" viewBox="0 0 16 16" width="14" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
              {ghConnecting ? 'Opening…' : 'Edit GitHub Access'}
            </button>
            <div className="am-gh-badge">✓ GitHub connected</div>
          </div>
          {ghConnecting && (
            <p className="am-hint" style={{ marginTop: '0.25rem' }}>
              Update repo access in the new tab, then return here.
            </p>
          )}

          <div className="am-field" style={{ marginTop: '0.75rem' }}>
            <div className="gh-label-row">
              <label className="am-label" htmlFor="am-new-repo">
                Repository {ghReposLoading && <span className="am-label-sub">loading…</span>}
              </label>
              <button
                type="button"
                className="am-refresh-btn"
                onClick={() => {
                  setGhReposLoading(true);
                  authFetch(`${BASE_GH()}/repos`, { method: 'GET' })
                    .then(r => r.ok ? r.json() : [])
                    .then(d => setGhRepos(Array.isArray(d) ? d : []))
                    .catch(() => {})
                    .finally(() => setGhReposLoading(false));
                }}
                disabled={ghReposLoading}
                title="Refresh repository list"
              >
                {ghReposLoading ? 'Loading…' : '↻ Refresh'}
              </button>
            </div>
            <select
              id="am-new-repo"
              className="dm-select am-select"
              value={selectedRepo}
              onChange={e => setSelectedRepo(e.target.value)}
              disabled={ghReposLoading}
            >
              <option value="">— select repo —</option>
              {ghRepos.map(r => (
                <option key={r.full_name} value={r.full_name}>{r.full_name}</option>
              ))}
            </select>
          </div>

          <div className="am-field">
            <label className="am-label">Branch</label>
            <input
              className="cm-input am-input"
              value={branchInput}
              onChange={e => setBranchInput(e.target.value)}
              placeholder="main"
              disabled={!selectedRepo}
              spellCheck={false}
            />
            <span className="am-hint">Type the exact branch name — it will be verified when you click Next.</span>
          </div>
        </>
      )}
    </div>
  );

  const renderStepConfigMap = () => (
    <div className="am-step">
      <h4 className="am-step-title">Step 3 — ConfigMap &amp; Secret <span className="am-step-optional">(both optional)</span></h4>
      <p className="am-hint">ConfigMaps hold environment variables for your deployment. You can skip this and configure it later.</p>

      <div className="am-cm-tabs">
        {[
          { id: 'skip', label: 'Skip' },
          { id: 'existing', label: 'Use Existing' },
          { id: 'upload', label: 'Upload .env' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            className={`am-cm-tab${cmAction === t.id ? ' active' : ''}`}
            onClick={() => { setCmAction(t.id); setCmErr(null); setCmMsg(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {cmAction === 'skip' && (
        <p className="am-hint" style={{ marginTop: '0.75rem' }}>No ConfigMap will be set. You can add one from Deployment Config.</p>
      )}

      {cmAction === 'existing' && (
        <div className="am-field" style={{ marginTop: '0.75rem' }}>
          <label className="am-label">Select ConfigMap</label>
          <select
            className="dm-select am-select"
            value={selectedCm}
            onChange={e => { setSelectedCm(e.target.value); setDeplCmRef(e.target.value); }}
          >
            <option value="">— choose —</option>
            {existingCms.map(cm => <option key={cm} value={cm}>{cm}</option>)}
          </select>
        </div>
      )}

      {cmAction === 'upload' && (
        <div style={{ marginTop: '0.75rem' }}>
          <div className="am-field">
            <label className="am-label">ConfigMap Name</label>
            <input
              className="cm-input am-input"
              value={cmName}
              onChange={e => setCmName(e.target.value)}
              placeholder={`${sanitize(newAppName) || 'my-app'}-config`}
            />
          </div>
          <div className="am-field">
            <label className="am-label">.env File</label>
            <div className="mcr-file-upload">
              <input
                id="am-cm-file"
                type="file"
                accept=".env,text/plain"
                onChange={e => setCmFile(e.target.files?.[0] || null)}
                className="mcr-file-input-hidden"
              />
              <button
                type="button"
                className="mcr-select-file-btn"
                onClick={() => document.getElementById('am-cm-file')?.click()}
              >
                Select file
              </button>
              {cmFile && <span className="mcr-file-name">{cmFile.name}</span>}
            </div>
          </div>
        </div>
      )}

      {cmMsg && <p className="cm-success" style={{ marginTop: '0.5rem' }}>{cmMsg}</p>}
      {cmErr && <p className="cm-error" style={{ marginTop: '0.5rem' }}>{cmErr}</p>}

      <h4 className="am-step-title" style={{ marginTop: '1.5rem' }}>
        Secret <span className="am-step-optional">(optional)</span>
      </h4>
      <p className="am-hint">
        Secrets hold sensitive environment variables — API keys, passwords, tokens. Values are
        stored encrypted and never shown again.
      </p>

      <div className="am-cm-tabs">
        {[
          { id: 'skip', label: 'Skip' },
          { id: 'existing', label: 'Use Existing' },
          { id: 'upload', label: 'Upload .env' },
        ].map(t => (
          <button
            key={t.id}
            type="button"
            className={`am-cm-tab${secAction === t.id ? ' active' : ''}`}
            onClick={() => { setSecAction(t.id); setSecErr(null); setSecMsg(null); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {secAction === 'skip' && (
        <p className="am-hint" style={{ marginTop: '0.75rem' }}>No Secret will be set. You can add one from Deployment Config.</p>
      )}

      {secAction === 'existing' && (
        <div className="am-field" style={{ marginTop: '0.75rem' }}>
          <label className="am-label">Select Secret</label>
          <select
            className="dm-select am-select"
            value={selectedSec}
            onChange={e => { setSelectedSec(e.target.value); setDeplSecRef(e.target.value); }}
          >
            <option value="">— choose —</option>
            {existingSecrets.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {secAction === 'upload' && (
        <div style={{ marginTop: '0.75rem' }}>
          <div className="am-field">
            <label className="am-label">Secret Name</label>
            <input
              className="cm-input am-input"
              value={secName}
              onChange={e => setSecName(e.target.value)}
              placeholder={`${sanitize(newAppName) || 'my-app'}-secret`}
            />
          </div>
          <div className="am-field">
            <label className="am-label">.env File</label>
            <div className="mcr-file-upload">
              <input
                id="am-sec-file"
                type="file"
                accept=".env,text/plain"
                onChange={e => setSecFile(e.target.files?.[0] || null)}
                className="mcr-file-input-hidden"
              />
              <button
                type="button"
                className="mcr-select-file-btn"
                onClick={() => document.getElementById('am-sec-file')?.click()}
              >
                Select file
              </button>
              {secFile && <span className="mcr-file-name">{secFile.name}</span>}
            </div>
          </div>
        </div>
      )}

      {secMsg && <p className="cm-success" style={{ marginTop: '0.5rem' }}>{secMsg}</p>}
      {secErr && <p className="cm-error" style={{ marginTop: '0.5rem' }}>{secErr}</p>}
    </div>
  );

  const renderStepDeployConfig = () => (
    <div className="am-step">
      <h4 className="am-step-title">Step 4 — Deployment Config</h4>
      <p className="am-hint">These settings define how your app runs in Kubernetes.</p>

      <div className="dm-row2">
        <div className="am-field">
          <label className="am-label">Replicas</label>
          <input
            className="cm-input dm-input dm-input-sm"
            type="number"
            min="1"
            value={deplReplicas}
            onChange={e => setDeplReplicas(e.target.value)}
          />
        </div>
        <div className="am-field">
          <label className="am-label">Container Port</label>
          <input
            className="cm-input dm-input dm-input-sm dm-no-spinner"
            type="number"
            min="1"
            max="65535"
            value={deplPort}
            onChange={e => setDeplPort(e.target.value)}
          />
        </div>
      </div>

      <div className="am-field">
        <label className="am-label">ConfigMap</label>
        <select
          className="dm-select am-select"
          value={deplCmRef}
          onChange={e => setDeplCmRef(e.target.value)}
        >
          <option value="">— select a ConfigMap (optional) —</option>
          {existingCms.map(cm => <option key={cm} value={cm}>{cm}</option>)}
        </select>
        <span className="am-hint">If you created one in the previous step it appears here.</span>
      </div>

      <div className="am-field">
        <label className="am-label">Secret</label>
        <select
          className="dm-select am-select"
          value={deplSecRef}
          onChange={e => setDeplSecRef(e.target.value)}
        >
          <option value="">— select a Secret (optional) —</option>
          {existingSecrets.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span className="am-hint">Secret values override ConfigMap keys of the same name.</span>
      </div>

      {/* ── Custom host (optional) ── */}
      <div className="dm-row2">
        <div className="am-field">
          <label className="am-label">URL Prefix <span className="am-label-sub">(optional, no dots)</span></label>
          <input
            className="cm-input am-input"
            type="text"
            placeholder="prefix"
            value={deplUrlPrefix}
            onChange={e => setDeplUrlPrefix(e.target.value.replace(/\./g, ''))}
          />
        </div>
        <div className="am-field">
          <label className="am-label">Custom Domain <span className="am-label-sub">(optional)</span></label>
          <select
            className="cm-input am-input"
            value={deplCustomDomain}
            onChange={e => setDeplCustomDomain(e.target.value)}
          >
            <option value="">— None —</option>
            {verifiedDomains.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>
      {deplUrlPrefix && deplCustomDomain && (
        <p className="am-hint" style={{ marginTop: '0.35rem' }}>
          Host: <code>{deplUrlPrefix}.{deplCustomDomain}</code>
        </p>
      )}
      {!deplUrlPrefix && !deplCustomDomain && (
        <p className="am-hint" style={{ marginTop: '0.35rem' }}>
          Leave both blank to use the default <code>cluster-app.mermadicrunner.com</code> host.
        </p>
      )}
      {verifiedDomains.length === 0 && (
        <p className="am-hint" style={{ marginTop: '0.35rem' }}>
          No verified domains yet — add one under <strong>Apps → Domains</strong>.
        </p>
      )}

      {clusterTier && TIER_CAPS[clusterTier] && (() => {
        const caps = TIER_CAPS[clusterTier];
        return (
          <div className="am-field">
            <button
              type="button"
              className="dm-advanced-toggle"
              onClick={() => setAdvancedOpen(o => !o)}
            >
              {advancedOpen ? '▾' : '▸'} Advanced Settings
            </button>
            {advancedOpen && (
              <div className="dm-advanced-body">
                <p className="am-hint">
                  Resource limits per pod. Leave blank to use cluster defaults (500m CPU / 512Mi memory).
                  Max for <strong>{tierLabel(clusterTier)}</strong> tier: {caps.cpu_m}m CPU / {caps.memory_mi}Mi memory.
                </p>
                <div className="dm-row2">
                  <div className="am-field">
                    <label className="am-label">CPU Limit (millicores)</label>
                    <input
                      className="cm-input dm-input dm-input-sm dm-no-spinner"
                      type="number"
                      min="100"
                      max={caps.cpu_m}
                      step="100"
                      placeholder={`e.g. 500 (max ${caps.cpu_m})`}
                      value={deplCpuLimitM}
                      onChange={e => setDeplCpuLimitM(e.target.value)}
                    />
                  </div>
                  <div className="am-field">
                    <label className="am-label">Memory Limit (MiB)</label>
                    <input
                      className="cm-input dm-input dm-input-sm dm-no-spinner"
                      type="number"
                      min="128"
                      max={caps.memory_mi}
                      step="128"
                      placeholder={`e.g. 512 (max ${caps.memory_mi})`}
                      value={deplMemLimitMi}
                      onChange={e => setDeplMemLimitMi(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );

  const renderStepReview = () => (
    <div className="am-step">
      <h4 className="am-step-title">Step {stepsForMode.length} — Review & Activate</h4>
      <div className="am-review-grid">
        <span className="am-review-label">App</span>
        <span className="am-review-val"><code>{effectiveAppName}</code></span>
        <span className="am-review-label">Image tag</span>
        <span className="am-review-val"><code>{effectiveImageName}</code></span>
        <span className="am-review-label">Repository</span>
        <span className="am-review-val"><code>{selectedRepo || (previewLocked ? 'GitHub not connected' : '')}</code></span>
        <span className="am-review-label">Branch</span>
        <span className="am-review-val"><code>{branchInput.trim() || (previewLocked ? 'GitHub not connected' : '')}</code></span>
        {isNewApp && deplCmRef && (
          <>
            <span className="am-review-label">ConfigMap</span>
            <span className="am-review-val"><code>{deplCmRef}</code></span>
          </>
        )}
        {isNewApp && deplSecRef && (
          <>
            <span className="am-review-label">Secret</span>
            <span className="am-review-val"><code>{deplSecRef}</code></span>
          </>
        )}
        {isNewApp && !isJobType && (
          <>
            <span className="am-review-label">Replicas</span>
            <span className="am-review-val">{deplReplicas}</span>
            <span className="am-review-label">Port</span>
            <span className="am-review-val">{deplPort}</span>
            {(deplUrlPrefix && deplCustomDomain) && (
              <>
                <span className="am-review-label">Host</span>
                <span className="am-review-val"><code>{deplUrlPrefix}.{deplCustomDomain}</code></span>
              </>
            )}
          </>
        )}
        <span className="am-review-label">Pipeline Type</span>
        <span className="am-review-val">{isJobType ? 'Build Only (job image)' : 'Build & Deploy'}</span>
      </div>
      {!isJobType && (
        <div className="am-field" style={{ marginTop: '0.75rem' }}>
          <label className="am-label">Auto-deploy on build success</label>
          <div className="am-toggle-row">
            <button
              type="button"
              className={`am-toggle${autoDeploy ? ' am-toggle-on' : ''}`}
              onClick={() => setAutoDeploy(v => !v)}
              aria-pressed={autoDeploy}
            >
              <span className="am-toggle-knob" />
            </button>
            <span className="am-hint" style={{ marginLeft: '0.6rem' }}>
              {autoDeploy
                ? 'On — every successful build will redeploy the app automatically.'
                : 'Off — you will receive an email when the build is ready to deploy manually.'}
            </span>
          </div>
        </div>
      )}
      {isJobType && (
        <p className="am-hint" style={{ marginTop: '0.5rem' }}>
          Build Only — your image will be built and published to MCR. No deployment will be triggered.
        </p>
      )}
      {(() => {
        const running = automations.find(a => a.app_name === effectiveAppName);
        if (running && PIPELINE_RUNNING_STATUSES.has(running.status)) {
          return (
            <div className="am-pipeline-running-banner">
              <span className="am-pipeline-running-dot" />
              <span>
                A build is already <strong>{PIPELINE_STATUS[running.status]?.label ?? running.status}</strong> for this automation.
                Wait for it to finish before triggering a new run.
              </span>
            </div>
          );
        }
        return null;
      })()}
      {previewLocked && (
        <p className="am-hint" style={{ marginTop: '0.75rem' }}>
          You are on the shared cluster, so a Build &amp; Deploy pipeline cannot be saved or
          activated here. Go back to Step 1 and pick <strong>Build Only</strong> to build and push an
          image right now, or{' '}
          <Link className="inst-link" to="/cluster">create your own cluster</Link> to deploy.
        </p>
      )}
      {saveConfigErr && <p className="cm-error">{saveConfigErr}</p>}
      {saveConfigMsg && <p className="cm-success">{saveConfigMsg}</p>}
      {activateErr && <p className="cm-error">{activateErr}</p>}
      {activateMsg && <div className="cm-success am-activate-success">{activateMsg}</div>}
    </div>
  );

  const renderCurrentStep = () => {
    if (step === STEP_APP)    return renderStepApp();
    if (step === STEP_GH)     return renderStepGitHub();
    if (step === STEP_CM)     return renderStepConfigMap();
    if (step === STEP_DEPLOY) return renderStepDeployConfig();
    return renderStepReview();
  };

  const renderWizardNav = () => {
    const isFirst  = step === STEP_APP;
    const isLast   = step === STEP_REVIEW;
    const isCmStep = step === STEP_CM;
    const pipelineAlreadyRunning = isLast && PIPELINE_RUNNING_STATUSES.has(
      automations.find(a => a.app_name === effectiveAppName)?.status
    );
    const anyBusy  = activating || saveConfigBusy || pipelineAlreadyRunning;

    return (
      <div className="am-nav">
        {!isFirst && (
          <button type="button" className="am-btn-back" onClick={prevStep} disabled={anyBusy}>
            ← Back
          </button>
        )}
        <div style={{ flex: 1 }} />
        {stepErr && <span className="am-step-err">{stepErr}</span>}
        {isLast ? (
          <>
            <button
              type="button"
              className="dm-btn-secondary"
              onClick={handleSaveConfig}
              disabled={previewLocked || anyBusy}
              title={previewLocked ? 'Build & Deploy needs a dedicated cluster' : undefined}
            >
              {saveConfigBusy ? 'Saving…' : 'Save Config'}
            </button>
            <button
              type="button"
              className="am-btn-activate"
              onClick={handleActivate}
              disabled={previewLocked || anyBusy}
              title={previewLocked ? 'Build & Deploy needs a dedicated cluster' : undefined}
              style={{ marginLeft: '0.5rem' }}
            >
              {activating ? 'Activating…' : (activateMsg && !activateSuccess) ? '↺ Retry Build' : '▶ Activate Automation'}
            </button>
          </>
        ) : isCmStep ? (
          // Fixed label: the step holds two independent sections, each with its own Skip
          // tab, so no single wording could describe what this button does to both.
          <button type="button" className="am-btn-next" onClick={handleSaveCm} disabled={cmSaving}>
            {cmSaving ? 'Saving…' : 'Save & Next →'}
          </button>
        ) : (
          <button type="button" className="am-btn-next" onClick={nextStep} disabled={branchValidating}>
            {branchValidating ? 'Verifying…' : 'Next →'}
          </button>
        )}
      </div>
    );
  };

  // ── instruction panel ────────────────────────────────────────────────────
  // Map the active tab to a logical panel index. Job pipelines skip the
  // ConfigMap (2) and Deploy Config (3) panels, so remap their tabs.
  const activeStep = wizardOpen
    ? step
    : viewingAuto
      ? (viewIsJob ? [STEP_APP, STEP_GH, STEP_REVIEW][viewTab] : viewTab)
      : -1;
  const renderInstructions = () => {
    const panels = {
      0: (
        <>
          <h4>Automation Overview</h4>
          <p>
            Automation links a GitHub branch to one of your apps. Every time you push to that branch,
            the pipeline automatically:
          </p>
          <ol>
            <li>Downloads your code from GitHub</li>
            <li>Builds a Docker image and pushes it to your registry (MCR)</li>
            <li>Redeploys the app on your cluster with the new image</li>
          </ol>
          <hr className="inst-divider" />
          <p>If <strong>Auto Deploy</strong> is off, you will receive an email when the build succeeds instead of an automatic redeploy.</p>
          <hr className="inst-divider" />
          <p>You will be able to edit existing automation in <Link className="inst-link" to="/deployment/config">ConfigMap</Link> and <Link className="inst-link" to="/deployment/apps">Deployment Config</Link>.</p>
        </>
      ),
      1: (
        <>
          <h4>GitHub Connection</h4>
          <p>Select the repository and branch you want to watch for changes.</p>
          <div className="inst-field">
            <span className="inst-field-name">Repository</span>
            Choose from the repos you granted the GitHub App access to. Click <strong>Edit repo access</strong> to update the list.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Branch</span>
            Type the exact branch name (e.g. <code>main</code>, <code>develop</code>). The system validates it exists against the GitHub API.
          </div>
          <hr className="inst-divider" />
          <p>Only pushes to the configured branch trigger the pipeline — other branches are ignored.</p>
        </>
      ),
      2: (
        <>
          <h4>ConfigMap &amp; Secret</h4>
          <p>
            Both inject environment variables into your container at runtime. For each, select an
            existing one or create a new one from a <code>.env</code> file.
          </p>
          <div className="inst-field">
            <span className="inst-field-name">ConfigMap</span>
            Plain configuration — hostnames, feature flags, log levels. Values are readable
            afterwards in <Link className="inst-link" to="/deployment/config">ConfigMaps</Link>.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Secret</span>
            Sensitive values — API keys, passwords, tokens. Stored encrypted and never shown
            again; manage them in <Link className="inst-link" to="/deployment/secrets">Secrets</Link>.
          </div>
          <hr className="inst-divider" />
          <p>
            Both are optional — skip them if your app does not need env vars. Where a key exists
            in both, the Secret wins.
          </p>
        </>
      ),
      3: (
        <>
          <h4>Deployment Config</h4>
          <p>
            Maps your app to a Kubernetes deployment. This tells the runner which image to update on each build.
          </p>
          <div className="inst-field">
            <span className="inst-field-name">Image Name</span>
            The MCR image tag that the pipeline will build and push. Must match your registry entry.
          </div>
          <div className="inst-field">
            <span className="inst-field-name">Deployment Name</span>
            The Kubernetes deployment to update after a successful build.
          </div>
          <hr className="inst-divider" />
          <p>Saving here also updates your Deployment Config so both stay in sync.</p>
        </>
      ),
      4: (
        <>
          <h4>Activate</h4>
          <p>Review your settings and activate the automation pipeline.</p>
          <div className="inst-field">
            <span className="inst-field-name">Auto Deploy</span>
            When on, every successful build redeploys your app automatically. When off, you get an email notification instead and can redeploy manually.
          </div>
          <hr className="inst-divider" />
          <p>After activating, the first build is triggered immediately. If it fails, use <strong>Retry Build</strong> to re-run it once your repo is fixed.</p>
          <p>You can always come back to edit any step by clicking the tab icons at the top.</p>
        </>
      ),
    };
    const content = activeStep >= 0 ? panels[activeStep] : (
      <>
        <h4>Automation Management</h4>
        <p>Link a GitHub branch to your app so every push automatically builds and redeploys it.</p>
        <p>Click <strong>+ New</strong> to set up your first automation, or select an existing one from the list to view or edit it.</p>
      </>
    );
    return <div className="page-inst-panel page-inst-panel--wide">{content}</div>;
  };

  return (
    <div className="cm-page">
      <h2 className="cm-title">Automation Management</h2>
      <p className="cm-subtitle">Link a GitHub branch to an app — every push builds and redeploys automatically.</p>

      <div className="cm-layout">
        {/* ── sidebar ── */}
        <div className="cm-sidebar">
          <div className="cm-sidebar-header">
            <span>Automations</span>
            <button className="cm-btn cm-btn-primary cm-btn-sm" onClick={openWizard}>+ New</button>
          </div>

          {autoLoading && <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>Loading…</p>}
          {!autoLoading && automations.length === 0 && (
            <p className="cm-hint" style={{ padding: '0.5rem 1rem' }}>No automations yet.</p>
          )}

          <ul className="cm-list">
            {automations.map(a => {
              const s = pipelineStatusFor(a.status, a.image_type === 'short');
              const hoverTitle = `${a.repo || ''}${a.branch ? ` @${a.branch}` : ''}`.trim() || a.app_name;
              return (
                <li
                  key={a.app_name}
                  className={`cm-list-item cm-list-item-stacked${viewingAuto?.app_name === a.app_name ? ' active' : ''}`}
                  onClick={() => openAutomation(a)}
                  title={hoverTitle}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="am-item-top-row">
                    <span className="cm-list-name">{a.app_name}</span>
                    <div className="am-item-top-right">
                      {a.status && (
                        <span className={`am-status-badge ${s ? s.cls : ''}`}>
                          {s ? s.label : a.status}
                        </span>
                      )}
                      <button
                        type="button"
                        className="am-sidebar-delete"
                        onClick={e => { e.stopPropagation(); setDeleteAutoConfirm(a.app_name); }}
                        disabled={(isPreview && a.image_type !== 'short') || deletingAuto === a.app_name}
                        title={isPreview && a.image_type !== 'short'
                          ? 'Build & Deploy needs a dedicated cluster'
                          : 'Remove automation'}
                      >
                        {deletingAuto === a.app_name ? '…' : '✕'}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* ── right panel ── */}
        <div className="cm-detail" style={{ minHeight: 480 }}>
          {wizardOpen ? (
            <div className="am-wizard">
              <div className="am-wizard-header">
                <h3 className="dm-panel-title">New Automation</h3>
                <button className="cm-btn cm-btn-ghost cm-btn-sm" onClick={closeWizard}>✕</button>
              </div>
              {renderProgress()}
              {renderCurrentStep()}
              {renderWizardNav()}
            </div>
          ) : viewingAuto ? (
            viewMode === 'select' ? renderActionChooser() : renderViewPanel()
          ) : (
            <div className="cm-empty-state">
              <p>Select an automation to view its config, or click <strong>+ New</strong> to create one.</p>
              <p className="cm-hint">Each push to your configured branch will trigger a CI build and redeploy your app.</p>
            </div>
          )}
        </div>

        {/* ── guide panel ── */}
        {renderInstructions()}
      </div>

      {/* ── delete confirmation ── */}
      {deleteAutoConfirm && (
        <div className="cm-modal-overlay" onClick={() => setDeleteAutoConfirm(null)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <h4 className="cm-modal-title">Delete Automation</h4>
            <p className="cm-modal-body">
              Remove automation for <strong>{deleteAutoConfirm}</strong>?
              Future pushes to the linked branch will no longer trigger builds.
            </p>
            <div className="cm-modal-buttons">
              <button className="cm-btn cm-btn-ghost" onClick={() => setDeleteAutoConfirm(null)}>Cancel</button>
              <button className="dm-btn-delete" onClick={() => handleDeleteAuto(deleteAutoConfirm)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
